import Icons from "@/assets/icons";
import Images from "@/assets/images";
import { ActionButton } from "@/components/ActionButton";
import { AppText } from "@/components/AppText";
import Header from "@/components/ui/Header";
import { Colors } from "@/constants/Colors";
import SubscriptionServices from "@/services/subscriptionServices";
import { useQuery } from "@tanstack/react-query";
import { router, useLocalSearchParams } from "expo-router";
import React, { useEffect, useState } from "react";
import { FlatList, ScrollView, TouchableOpacity, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Image } from "expo-image";
import { FontFamilies } from "@/constants/Typography";
import { Ionicons } from "@expo/vector-icons";
import usePlaid from "@/hooks/sharedHook/usePlaid";
import FBTrackingService from "@/services/fbTrackingService";
import useWindow from "@/hooks/useWindow";
import { StyleSheet, scale } from "@/utils/appStyleSheet";
import useUserStore from "@/store/user";

export enum SubscriptionSetupType {
  Subscription = "subscription",
  Spending = "spending",
  MyMoney = "my-money",
}

export default function SubscriptionSetupScreen() {
  const params = useLocalSearchParams();
  const isSpending = params?.type === SubscriptionSetupType.Spending;
  const { isDesktop } = useWindow();
  const { userProfile } = useUserStore();
  const { data: financeAccounts, refetch } = useQuery({
    queryKey: ["financeAccounts"],
    queryFn: SubscriptionServices.getListAccounts,
  });

  const [linkToken, setLinkToken] = useState<string>();
  const { open: openPlaid, ready } = usePlaid({
    token: linkToken,
    onSuccess: async ({ metadata, publicToken }) => {
      const resCreateAccount = await SubscriptionServices.createAccount({
        publicToken,
        metadata,
        userId: userProfile?.id,
      });
      if (resCreateAccount.success) {
        console.log("Account created", resCreateAccount.data);
        SubscriptionServices.createSubscription(userProfile?.id);
        refetch();
      } else {
        console.log("Account creation failed", resCreateAccount.message);
      }
    },
  });
  useEffect(() => {
    if (linkToken && ready) {
      openPlaid();
    }
  }, [linkToken, ready]);
  useEffect(() => {
    FBTrackingService.trackViewContent("subscription-setup");
  }, []);

  const onNext = () => {
    if (hasAccounts) {
      router.push("/my-money/analyzing");
      return;
    }
  };

  const onPressActionButton = async () => {
    const resTokenLink = await SubscriptionServices.getPlaidLinkToken(userProfile?.id);
    if (resTokenLink.success) {
      const { linkToken } = resTokenLink.data;
      setLinkToken(linkToken);
    }
  };
  const accountCount = (financeAccounts?.data?.data || []).reduce(
    (total: number, item: { accountCount?: number }) => total + (item.accountCount || 0),
    0
  );
  const hasAccounts = accountCount > 0;

  return (
    <SafeAreaView
      style={[styles.container, isDesktop && styles.containerDesktop]}
    >
      <Header title={isSpending ? "Start monitoring" : "Link your accounts"} />
      <View style={styles.contentContainer}>
        <ScrollView contentContainerStyle={styles.scrollContent}>
          {/* App Icons Grid */}
          {!hasAccounts && (
            <Image
              source={Images.subscriptionBanner}
              style={styles.subscriptionBanner}
            />
          )}

          {/* Main Content */}
          <View style={styles.mainContent}>
            <AppText style={styles.title}>
              {isSpending
                ? "Start monitoring your spending"
                : "Link your accounts"}
            </AppText>
            <AppText style={styles.subtitle}>
              Link your accounts so we can detect subscriptions & help you save
              money
            </AppText>
            {/* Account Section */}
            <View style={styles.accountSection}>
              <View style={styles.accountRow}>
                <View style={styles.accountInfo}>
                  <View style={styles.bankIcon}>
                    <Icons.Bank width={scale(24)} height={scale(24)} />
                  </View>
                  <AppText style={styles.accountType}>
                    {accountCount
                      ? `Accounts linked`
                      : "Checking"}
                  </AppText>
                </View>
                {!hasAccounts && (
                  <View style={styles.accountStatus}>
                    <AppText style={styles.requiredText}>Required</AppText>
                  </View>
                )}
              </View>
              {hasAccounts && <View style={styles.seperator} />}
              <FlatList
                style={styles.accountList}
                data={financeAccounts?.data?.data || []}
                renderItem={({ item }) => (
                  <View style={styles.accountRow}>
                    <AppText style={styles.accountName}>{item.name}</AppText>
                    <AppText style={styles.accountCount}>
                      {item.accountCount} accounts
                    </AppText>
                  </View>
                )}
              />
            </View>
            {hasAccounts && (
              <TouchableOpacity
                style={styles.addBankButton}
                onPress={onPressActionButton}
              >
                <View style={styles.addBankIconContainer}>
                  <Ionicons name="add" size={scale(24)} color="#0F9129" />
                </View>
                <AppText style={styles.addBankText}>
                  Link another account
                </AppText>
              </TouchableOpacity>
            )}
          </View>
        </ScrollView>

        {/* Security Info */}
        <View style={styles.securityInfo}>
          <Icons.ShieldCheck width={scale(24)} height={scale(24)} />
          <AppText style={styles.securityText}>
            Your connection is secured with 256-bit encryption and Plaid.
          </AppText>
        </View>
        {/* Connect Button */}
        <View style={styles.buttonContainer}>
          <ActionButton
            title={
              hasAccounts
                ? `Continue with ${accountCount} account${accountCount !== 1 ? "s" : ""}`
                : "Connect my bank account"
            }
            type="primary"
            icon={
              !hasAccounts && (
                <View style={styles.iconButton}>
                  <Icons.Plus width={scale(16)} height={scale(16)} />
                </View>
              )
            }
            onPress={hasAccounts ? onNext : onPressActionButton}
            style={styles.connectButton}
          />
        </View>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  containerDesktop: {
    maxWidth: 600,
    alignSelf: "center",
  },
  contentContainer: {
    flex: 1,
  },
  scrollContent: {
    flex: 1,
  },
  subscriptionBanner: {
    width: "100%",
    aspectRatio: 16 / 9,
    marginBottom: 24,
  },
  mainContent: {
    marginBottom: 40,
    marginTop: 16,
    gap: 16,
    paddingHorizontal: 16,
  },
  title: {
    fontSize: 30,
    fontFamily: FontFamilies.InterTight.Medium,
    color: "#161D14",
    lineHeight: 40,
  },
  subtitle: {
    fontSize: 15,
    color: "#161D14",
    fontFamily: FontFamilies.Inter.Regular,
  },
  accountSection: {
    backgroundColor: "#FFFFFF",
    marginTop: 12,
    borderRadius: 16,
    paddingVertical: 18,
    paddingHorizontal: 16,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  accountList: {},
  accountRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  accountInfo: {
    flexDirection: "row",
    alignItems: "center",
  },
  bankIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    justifyContent: "center",
    alignItems: "center",
  },
  accountType: {
    fontSize: 16,
    color: "#1F2937",
    fontFamily: FontFamilies.InterTight.SemiBold,
  },
  accountName: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
  },
  accountCount: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
  },
  accountStatus: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  requiredText: {
    fontSize: 16,
    color: "#161D14",
    fontFamily: FontFamilies.Inter.Regular,
  },
  securityInfo: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
    paddingHorizontal: 16,
  },
  securityText: {
    flexShrink: 1,
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    marginLeft: 12,
  },
  buttonContainer: {
    paddingHorizontal: 16,
    marginBottom: 16,
  },
  connectButton: {
    width: "100%",
    height: 56,
    borderRadius: 28,
  },
  iconButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: "#FFFFFF1F",
    justifyContent: "center",
    alignItems: "center",
  },
  seperator: {
    height: 1,
    width: "100%",
    backgroundColor: "#CFD4D9",
    marginVertical: 12,
  },
  addBankButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F8F9FA",
    borderRadius: 12,
  },
  addBankIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#E8F5E8",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  addBankText: {
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#0F9129",
    fontWeight: "600",
  },
});
